class FeatureSlideshow extends HTMLElement {
    constructor() {
        super();
        this.currentIndex = 0;
        this.duration = 6000; // ms per slide
        this._timer = null;
        this._raf = null;
        this._startTime = null;
        this._endedAbort = null;
        this._pausedAt = null;
    }

    connectedCallback() {
        this.navItems = Array.from(this.querySelectorAll('.feature-slideshow__nav-item'));
        this.slides = Array.from(this.querySelectorAll('.feature-slideshow__slide'));
        this.circles = Array.from(this.querySelectorAll('.feature-slideshow__circle-fill'));

        const r = 11;
        this.circumference = 2 * Math.PI * r;

        this.circles.forEach((c) => {
            c.style.strokeDasharray = this.circumference;
            c.style.strokeDashoffset = 0; // start empty
        });

        this.navItems.forEach((btn, i) => {
            btn.addEventListener('click', () => this.goTo(i));
        });

        this._onVisibilityChange = () => {
            if (document.hidden) {
                this._pausedAt = performance.now();
                this._clearTimer();
            } else {
                if (this._pausedAt && this._startTime) {
                    // Shift startTime forward by however long we were hidden
                    const hiddenDuration = performance.now() - this._pausedAt;
                    this._startTime += hiddenDuration;
                    this._pausedAt = null;
                }
                this._animate();
            }
        };

        document.addEventListener('visibilitychange', this._onVisibilityChange);

        this.goTo(0);
    }

    disconnectedCallback() {
        this._clearTimer();
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    /**
     * @param {number} index
     */
    goTo(index) {
        this._clearTimer();

        // Abort any previous ended listener
        if (this._endedAbort) {
            this._endedAbort.abort();
        }
        this._endedAbort = new AbortController();

        // Deactivate outgoing
        const outgoingSlide = this.slides[this.currentIndex];
        const outgoingVideo = outgoingSlide?.querySelector('video');

        if (outgoingVideo) {
            outgoingVideo.pause();
            outgoingVideo.currentTime = 0;
        }

        outgoingSlide?.classList.remove('is-active');
        outgoingSlide?.setAttribute('aria-hidden', 'true');


        this.navItems[this.currentIndex]?.classList.remove('is-active');
        this.navItems[this.currentIndex]?.setAttribute('aria-selected', 'false');

        this.circles[this.currentIndex].style.strokeDashoffset = 0;
        this.circles[index].style.strokeDashoffset = this.circumference;

        this.currentIndex = index;

        this.navItems[index].classList.add('is-active');
        this.navItems[index].setAttribute('aria-selected', 'true');
        this.slides[index].classList.add('is-active');
        this.slides[index].setAttribute('aria-hidden', 'false');

        const incomingVideo = this.slides[index].querySelector('video');

        if (incomingVideo) {
            incomingVideo.currentTime = 0;
            incomingVideo.play();

            incomingVideo.addEventListener('ended', () => {
                const next = (this.currentIndex + 1) % this.navItems.length;
                this.goTo(next);
            }, { once: true, signal: this._endedAbort.signal });

            const setDuration = () => {
                this.duration = incomingVideo.duration * 1000;
                this._startTime = null;
                this._animate();
            };

            if (incomingVideo.readyState >= 1) {
                setDuration();
            } else {
                incomingVideo.addEventListener('loadedmetadata', setDuration, { once: true });
            }
        }
    }


    _animate() {
        this._raf = requestAnimationFrame((ts) => {
            if (!this._startTime) this._startTime = ts;
            const elapsed = ts - this._startTime;
            const progress = Math.min(elapsed / this.duration, 1);
            const offset = this.circumference * (1 - progress);

            this.circles[this.currentIndex].style.strokeDashoffset = offset;

            if (progress < 1) {
                this._animate();
            }
        });
    }

    _clearTimer() {
        if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }
}

customElements.define('feature-slideshow', FeatureSlideshow);